import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  UtensilsCrossed,
  Plus,
  Search,
  FileDown,
  RefreshCw,
  X,
  Check,
  ThumbsUp,
  ThumbsDown,
  BedDouble,
  Share2,
  Copy,
  LogOut as CheckOutIcon,
  Star,
  MessageCircle,
  Send,
  Flag,
  CalendarOff,
  CalendarClock,
  AlertTriangle,
  IndianRupee,
  FileText,
  UserX,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import BlockDatesModal from "../components/admin/BlockDatesModal";
import { downloadWithAuth } from "../utils/download";
import { openInvoicePreview } from "../utils/invoice";
import { toLocalISODate } from "../utils/date";
import { calcRoomTotal } from "../utils/pricing";
import { useSettings } from "../context/SettingsContext";
import { bookingLabel } from "../utils/bookingLabel";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000/api";
const BE = process.env.REACT_APP_BACKEND_URL || "";

// Matches the backend's normalizePhone (api.js) — last 10 digits, so a
// guest flag (keyed by phone) can be matched against any row's number
// regardless of +91/spacing formatting differences.
function normalizePhone(p) {
  return String(p || "").replace(/\D/g, "").slice(-10);
}

const STATUS_COLORS = {
  Pending: "bg-amber-50 text-amber-700 border-amber-200",
  Confirmed: "bg-green-50 text-green-700 border-green-200",
  Completed: "bg-blue-50 text-blue-700 border-blue-200",
  Cancelled: "bg-red-50 text-red-700 border-red-200",
  Blocked: "bg-gray-100 text-gray-600 border-gray-200",
  "No-Show": "bg-orange-50 text-orange-700 border-orange-200",
};

// Airbnb already collects payment upfront, so a no-show there costs
// nothing to track manually; Agoda's host/OTA payment split varies by
// listing, unlike these three, so it's left out too.
const NO_SHOW_SOURCES = ["Booking.com", "Website", "Manual"];

const SOURCE_DOT = {
  Airbnb: "bg-rose-400",
  "Booking.com": "bg-blue-400",
  Agoda: "bg-red-400",
  MakeMyTrip: "bg-indigo-400",
  Website: "bg-green-400",
  Manual: "bg-purple-400",
};

/* ── Approve modal ── */
function ApproveModal({ booking, onClose, onDone, authHeaders }) {
  const [totalAmount, setTotal] = useState(booking.payment?.totalAmount || "");
  const [advancePaid, setAdvance] = useState(
    booking.payment?.advancePaid || "",
  );
  const [remarks, setRemarks] = useState("");
  const [roomId, setRoomId] = useState(booking.room?._id || booking.room || "");
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [approved, setApproved] = useState(null); // holds the saved booking once approved

  // Load available rooms when modal opens
  React.useEffect(() => {
    const ci = toLocalISODate(booking.checkIn);
    const co = toLocalISODate(booking.checkOut);
    fetch(`${API}/rooms/availability?checkIn=${ci}&checkOut=${co}`)
      .then((r) => r.json())
      .then((j) => {
        setRooms(j.data || []);
        // Suggest a total based on room price × nights (adjusted for the
        // booking's guest count), minus any promo the guest applied —
        // admin can still edit before confirming.
        if (!totalAmount && roomId) {
          const room = (j.data || []).find((r) => r._id === roomId);
          if (room) {
            const suggested =
              calcRoomTotal(room, booking.guestCount, booking.checkIn, booking.checkOut) -
              (booking.discountAmount || 0);
            setTotal(Math.max(0, suggested));
          }
        }
      })
      .catch(() => {});
  }, []);

  async function submit() {
    setSaving(true);
    try {
      const res = await fetch(`${API}/bookings/${booking._id}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ totalAmount, advancePaid, remarks, roomId }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onDone(json.data);
      setApproved(json.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  const balance =
    (parseFloat(totalAmount) || 0) - (parseFloat(advancePaid) || 0);

  if (approved) {
    return <ApprovalWhatsAppShare booking={approved} onClose={onClose} justApproved />;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <ThumbsUp size={15} className="text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900">Approve Booking</h3>
          </div>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2">
            <strong>{booking.guest?.fullNames?.[0] || "Guest"}</strong> ·{" "}
            {new Date(booking.checkIn).toLocaleDateString("en-IN")} →{" "}
            {new Date(booking.checkOut).toLocaleDateString("en-IN")}
          </p>

          {booking.guestFlag && (
            <div className="flex items-start gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
              <Flag size={14} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">This guest was flagged before</p>
                <p className="text-red-600 mt-0.5">{booking.guestFlag.reason}</p>
              </div>
            </div>
          )}

          {booking.appliedPromoCode && (
            <p className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-xl px-3 py-2">
              Guest applied promo <strong>{booking.appliedPromoCode}</strong>{" "}
              (-₹{(booking.discountAmount || 0).toLocaleString("en-IN")}). Total
              below already accounts for it — adjust if needed.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Total Amount (₹)
              </label>
              <input
                type="number"
                value={totalAmount}
                onChange={(e) => setTotal(e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Advance Paid (₹)
              </label>
              <input
                type="number"
                value={advancePaid}
                onChange={(e) => setAdvance(e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-green-400"
              />
            </div>
          </div>

          {totalAmount && (
            <div className="flex justify-between text-sm bg-green-50 border border-green-200 rounded-xl px-3 py-2">
              <span className="text-gray-600">Balance Due</span>
              <span className="font-bold text-green-700">
                ₹{balance.toLocaleString("en-IN")}
              </span>
            </div>
          )}

          {/* Room assignment */}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center gap-1">
              <BedDouble size={12} className="text-amber-500" /> Assign Room
            </label>
            {rooms.length === 0 ? (
              <p className="text-xs text-amber-600 bg-amber-50 rounded-xl px-3 py-2">
                ⚠ No rooms configured. Add rooms in the Rooms section first.
              </p>
            ) : (
              <select
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                  focus:outline-none focus:ring-2 focus:ring-green-400 bg-white"
              >
                <option value="">— Select Room (optional) —</option>
                {rooms.map((r) => (
                  <option key={r._id} value={r._id} disabled={!r.available}>
                    {r.name} ({r.type}){" "}
                    {!r.available ? "— BOOKED" : `— ₹${r.pricePerNight}/night`}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Remarks (optional)
            </label>
            <input
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Any notes for guest…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>

          <p className="text-xs text-gray-400">
            Guest will receive a WhatsApp confirmation automatically.
          </p>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-green-500 text-white text-sm font-medium
              hover:bg-green-600 disabled:opacity-60 flex items-center justify-center gap-2"
          >
            <Check size={14} /> {saving ? "Approving…" : "Approve & Notify"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Post-approval WhatsApp share screen — the guest already gets an
   automatic confirmation (via whatsappService.js on the backend), but the
   admin can also send the same details themselves, from their own
   WhatsApp number, with one click. ── */
function ApprovalWhatsAppShare({ booking, onClose, justApproved }) {
  const { hotelName, address, mapsUrl, phone, phone2 } = useSettings();
  const guestName = booking.guest?.fullNames?.[0] || "Guest";
  const nights = Math.max(
    1,
    Math.ceil((new Date(booking.checkOut) - new Date(booking.checkIn)) / 86400000),
  );
  const fmtDate = (d) =>
    new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

  const addressLines = [address, mapsUrl].filter(Boolean).join("\n");
  const contactLine   = [phone, phone2].filter(Boolean).join(" / ");

  // The server saves this exact text at approval time (see /bookings/:id/approve)
  // so it's still here later even if amounts changed since — fall back to
  // rebuilding it client-side only for old bookings approved before that existed.
  const message = booking.guestMessageText || `✅ *Booking Confirmed — ${hotelName}*

Hi ${guestName}! Your booking is *confirmed*! 🎉

📋 *Ref:*       ${booking.bookingRef}
📅 *Check-in:*  ${fmtDate(booking.checkIn)}
📅 *Check-out:* ${fmtDate(booking.checkOut)}
🌙 *Nights:*    ${nights}
👥 *Guests:*    ${booking.guestCount || 1}
💰 *Total:*     ₹${(booking.payment?.totalAmount || 0).toLocaleString("en-IN")}
💳 *Advance:*   ₹${(booking.payment?.advancePaid || 0).toLocaleString("en-IN")}
💵 *Balance:*   ₹${(booking.payment?.remainingBalance || 0).toLocaleString("en-IN")}
${addressLines ? `\n📍 *Address:*\n${addressLines}\n` : ""}${contactLine ? `\n📞 *Contact:* ${contactLine}\n` : ""}
Please carry a valid photo ID at check-in.
See you soon! 🏡`;

  // Editable, not locked to the guest's own number — if the primary
  // WhatsApp Business sending number is ever blocked/restricted, this same
  // saved message can still go out manually via any other WhatsApp number.
  const [waNumber, setWaNumber] = useState((booking.guest?.whatsapp || "").replace(/[^0-9]/g, ""));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Check size={15} className="text-green-600" />
            </div>
            <h3 className="font-bold text-gray-900">
              {justApproved ? "Booking Approved" : "WhatsApp Confirmation"}
            </h3>
          </div>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500">
            {justApproved
              ? "An automatic WhatsApp confirmation was already sent. You can also send it yourself, from your own number, below."
              : "Saved from when this booking was approved — send it again, from any WhatsApp number, if needed (e.g. the business number is blocked or the first send didn't land)."}
          </p>
          <textarea
            readOnly
            value={message}
            rows={11}
            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-700
              focus:outline-none resize-none bg-gray-50 font-mono"
          />
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Send to (WhatsApp number — edit to use a different one)
            </label>
            <input
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="91XXXXXXXXXX"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-green-400"
            />
          </div>
        </div>

        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            Done
          </button>
          <a
            href={waNumber ? `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}` : undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!waNumber}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-2
              ${waNumber
                ? "bg-green-500 text-white hover:bg-green-600"
                : "bg-gray-100 text-gray-400 pointer-events-none"}`}
          >
            <MessageCircle size={14} /> Send via WhatsApp
          </a>
        </div>
      </div>
    </div>
  );
}

/* ── Flag a guest for misconduct/misbehavior/property damage — keyed by
   phone number so it follows the same person across separate bookings
   (every booking creates its own fresh Guest sub-document — see the
   backend GuestFlag schema comment). ── */
function FlagGuestModal({ phone, guestName, existingFlag, onClose, onSaved, authHeaders }) {
  const [reason, setReason] = useState(existingFlag?.reason || "");
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState("");

  async function save() {
    if (!reason.trim()) { setError("A reason is required."); return; }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/guest-flags`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ phone, reason: reason.trim() }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(json.data);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function remove() {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/guest-flags/${phone}`, { method: "DELETE", headers: authHeaders() });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(null);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <Flag size={16} className="text-red-500" /> Flag Guest
          </h3>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2">
            <strong>{guestName}</strong> · {phone}
          </p>
          <p className="text-xs text-gray-500">
            This follows the phone number across any future booking, so it'll surface again if they book with us again.
          </p>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Reason (misconduct, misbehavior, property damage, etc.)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Describe what happened…"
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
          </div>
          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
          )}
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          {existingFlag && (
            <button
              onClick={remove}
              disabled={saving}
              className="flex-1 py-2.5 rounded-xl border border-red-200 text-red-600 text-sm font-medium
                hover:bg-red-50 disabled:opacity-60"
            >
              Remove
            </button>
          )}
          <button
            onClick={save}
            disabled={saving || !reason.trim()}
            className="flex-1 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium
              hover:bg-red-600 disabled:opacity-60"
          >
            {saving ? "Saving…" : existingFlag ? "Update" : "Flag Guest"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Reject modal ── */
/* ── Generic reason-required modal (reject / cancel) ── */
function ReasonModal({
  booking,
  onClose,
  onDone,
  authHeaders,
  endpoint,
  title,
  verb,
  savingVerb,
  notifiesGuest = true,
  Icon = ThumbsDown,
  iconClasses = "bg-red-100 text-red-500",
  buttonClasses = "bg-red-500 hover:bg-red-600",
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/bookings/${booking._id}/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ reason }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onDone(json.data);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${iconClasses}`}>
              <Icon size={15} />
            </div>
            <h3 className="font-bold text-gray-900">{title}</h3>
          </div>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2">
            <strong>{booking.guest?.fullNames?.[0] || "Guest"}</strong> ·{" "}
            {new Date(booking.checkIn).toLocaleDateString("en-IN")} →{" "}
            {new Date(booking.checkOut).toLocaleDateString("en-IN")}
          </p>
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              {notifiesGuest ? "Reason (sent to guest)" : "Note (internal only)"}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder={notifiesGuest ? "e.g. Dates not available, property maintenance…" : "e.g. No response to calls, room found empty at check-in…"}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
            />
          </div>
          <p className="text-xs text-gray-400">
            {notifiesGuest ? "Guest will receive a WhatsApp message." : "Not sent to the guest — for your own records only."}
          </p>
          {error && (
            <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">
              {error}
            </p>
          )}
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            Back
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className={`flex-1 py-2.5 rounded-xl text-white text-sm font-medium
              disabled:opacity-60 ${buttonClasses}`}
          >
            {saving ? savingVerb : verb}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Manual Booking Modal ── */
function ManualBookingModal({ onClose, onSaved, authHeaders }) {
  const [form, setForm] = useState({
    // One entry per guest staying. The room's capacity decides how many of
    // these the form collects, which is why the room is chosen first.
    guestNames: [""],
    guestCount: 1,
    whatsapp: "",
    email: "",
    travelFrom: "",
    travelTo: "",
    roomId: "",
    unitId: "",
    roomCount: 1,
    checkIn: "",
    checkOut: "",
    totalAmount: "",
    advancePaid: "",
    paymentMethod: "Cash",
    status: "Confirmed",
    remarks: "",
    // Ticked by default: a keyed-in booking is as real as a website one, so
    // the guest normally should get their confirmation and invoice. Unticking
    // it still files the invoice — it just isn't sent.
    notifyGuest: true,
  });
  const [rooms, setRooms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Once both dates are set, switch from the plain room list to the same
  // availability-aware endpoint the public booking form uses — this is
  // what lets the dropdown show "X of Y available" per type, and stops an
  // admin from silently double-booking a room here.
  useEffect(() => {
    if (form.checkIn && form.checkOut) {
      fetch(`${API}/rooms/availability?checkIn=${form.checkIn}&checkOut=${form.checkOut}`)
        .then((r) => r.json())
        .then((j) => setRooms(j.data || []))
        .catch(() => {});
    } else {
      fetch(`${API}/rooms`, { headers: authHeaders() }).then((r) => r.json()).then((j) => setRooms(j.data || [])).catch(() => {});
    }
  }, [form.checkIn, form.checkOut, authHeaders]);

  const selectedType = rooms.find((r) => r._id === form.roomId);
  const activeUnits = (selectedType?.units || []).filter((u) => u.active !== false);
  const maxRoomCount = selectedType?.availableCount ?? activeUnits.length ?? 1;

  // Capacity is per room, so booking several rooms of a type raises the
  // ceiling proportionally. With no room chosen yet there is nothing to
  // cap against, so the guest fields stay hidden until there is.
  const roomsBooked     = form.unitId ? 1 : (form.roomCount || 1);
  const perRoomCapacity = selectedType?.capacity || 0;
  const maxGuests       = perRoomCapacity * roomsBooked;

  // Changing room or room count can leave a headcount that no longer fits.
  // Clamp it (and drop the now-surplus name boxes) rather than letting the
  // form sit in a state the server will reject.
  useEffect(() => {
    if (!maxGuests) return;
    setForm(f => {
      if (f.guestCount <= maxGuests) return f;
      return { ...f, guestCount: maxGuests, guestNames: f.guestNames.slice(0, maxGuests) };
    });
  }, [maxGuests]);

  function setGuestCount(n) {
    setForm(f => {
      const names = [...f.guestNames];
      while (names.length < n) names.push("");
      return { ...f, guestCount: n, guestNames: names.slice(0, n) };
    });
  }
  function setGuestName(i, v) {
    setForm(f => {
      const names = [...f.guestNames];
      names[i] = v;
      return { ...f, guestNames: names };
    });
  }

  const total = parseFloat(form.totalAmount) || 0;
  const advance = parseFloat(form.advancePaid) || 0;

  async function submit() {
    if (!form.guestNames[0]?.trim() || !form.checkIn || !form.checkOut) {
      setError("The lead guest's name, check-in and check-out are required.");
      return;
    }
    if (maxGuests && form.guestCount > maxGuests) {
      setError(`This room holds up to ${maxGuests} guest${maxGuests !== 1 ? "s" : ""}.`);
      return;
    }
    const waDigits = (form.whatsapp || "").replace(/\D/g, "");
    if (waDigits.length < 7 || waDigits.length > 15) {
      setError("Enter a valid WhatsApp number.");
      return;
    }
    // Loose shape check on purpose — the server applies the same one, and
    // over-strict email patterns reject valid addresses.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || "").trim())) {
      setError("A valid email address is required — the confirmation and invoice go there.");
      return;
    }
    // <input type="date"> can silently produce a date like 0026-12-06
    // instead of 2026-12-06 if the year segment isn't fully typed before
    // the field loses focus — JS parses it as a valid but ~2000-year-off
    // date with no error, which then blows up the nights calculation.
    const thisYear = new Date().getFullYear();
    const checkInYear  = new Date(form.checkIn).getFullYear();
    const checkOutYear = new Date(form.checkOut).getFullYear();
    if (checkInYear < thisYear - 1 || checkInYear > thisYear + 5 ||
        checkOutYear < thisYear - 1 || checkOutYear > thisYear + 5) {
      setError("Check-in/check-out year looks wrong — please re-enter the dates.");
      return;
    }
    if (new Date(form.checkOut) <= new Date(form.checkIn)) {
      setError("Check-out must be after check-in.");
      return;
    }
    if (total < 0 || advance < 0) {
      setError("Total and advance amounts can't be negative.");
      return;
    }
    if (total > 0 && advance > total) {
      setError("Advance can't be more than the total amount.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API}/bookings/manual`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify(form),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      onSaved(json.data.bookings || [json.data.booking]);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  function field(label, key, type = "text", opts = {}) {
    return (
      <div>
        <label
          htmlFor={`manual-${key}`}
          className="block text-xs font-medium text-gray-500 mb-1"
        >
          {label}
        </label>
        <input
          id={`manual-${key}`}
          name={key}
          type={type}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
            focus:outline-none focus:ring-2 focus:ring-amber-400"
          {...opts}
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/80 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            Manual / Advance Booking
          </h2>
          <button onClick={onClose}>
            <X size={16} className="text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {/* Both required: a manual booking gets the same confirmation,
              invoice and checkout message as any other, and each needs
              somewhere to go. Previously there was no email field at all. */}
          <div className="grid grid-cols-2 gap-3">
            {field("WhatsApp *", "whatsapp", "tel", {
              placeholder: "+91 9876543210",
            })}
            {field("Email *", "email", "email", {
              placeholder: "guest@example.com",
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("Travel From", "travelFrom", "text", {
              placeholder: "e.g. Kolkata",
            })}
            {field("Travel To", "travelTo", "text", {
              placeholder: "e.g. Guwahati",
            })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {field("Check-in *", "checkIn", "date")}
            {field("Check-out *", "checkOut", "date")}
          </div>
          <div>
            <label htmlFor="manual-room" className="block text-xs font-medium text-gray-500 mb-1">
              Room Type
            </label>
            <select
              id="manual-room"
              value={form.roomId}
              onChange={(e) => setForm((f) => ({ ...f, roomId: e.target.value, unitId: "", roomCount: 1 }))}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
            >
              <option value="">— Select room type (optional) —</option>
              {rooms.map((r) => {
                const countLabel = form.checkIn && form.checkOut && r.totalUnits > 1 ? ` — ${r.availableCount} of ${r.totalUnits} available` : "";
                return (
                  <option key={r._id} value={r._id} disabled={r.available === false}>
                    {r.name} ({r.type}){countLabel}{r.available === false ? " — SOLD OUT" : ""}
                  </option>
                );
              })}
            </select>
            <p className="text-[11px] text-gray-400 mt-1">
              {form.checkIn && form.checkOut
                ? "Types fully booked for these dates are disabled."
                : "Enter dates above to see live availability."}
            </p>
          </div>

          {selectedType && activeUnits.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="manual-unit" className="block text-xs font-medium text-gray-500 mb-1">
                  Specific Room #
                </label>
                <select
                  id="manual-unit"
                  value={form.unitId}
                  onChange={(e) => setForm((f) => ({ ...f, unitId: e.target.value, roomCount: 1 }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                >
                  <option value="">Auto-assign</option>
                  {activeUnits.map((u) => (
                    <option key={u._id} value={u._id}>{u.roomNumber || "(no number)"}</option>
                  ))}
                </select>
              </div>
              {!form.unitId && maxRoomCount > 1 && (
                <div>
                  <label htmlFor="manual-room-count" className="block text-xs font-medium text-gray-500 mb-1">
                    Number of Rooms
                  </label>
                  <select
                    id="manual-room-count"
                    value={form.roomCount}
                    onChange={(e) => setForm((f) => ({ ...f, roomCount: parseInt(e.target.value) }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  >
                    {Array.from({ length: maxRoomCount }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} room{n > 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
          {/* Guests — deliberately after the room, because the room's capacity
              is what decides how many can be entered. Before this the form
              only ever captured one name, so a family of four was recorded
              as a single guest and every headcount downstream was wrong. */}
          {selectedType ? (
            <div className="rounded-xl border border-gray-200 bg-white/60 p-3 space-y-3">
              <div className="flex items-end justify-between gap-3">
                <div className="flex-1">
                  <label htmlFor="manual-guest-count" className="block text-xs font-medium text-gray-500 mb-1">
                    Number of Guests *
                  </label>
                  <select
                    id="manual-guest-count"
                    value={form.guestCount}
                    onChange={(e) => setGuestCount(parseInt(e.target.value, 10))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  >
                    {Array.from({ length: Math.max(1, maxGuests) }, (_, i) => i + 1).map((n) => (
                      <option key={n} value={n}>{n} guest{n > 1 ? "s" : ""}</option>
                    ))}
                  </select>
                </div>
                <p className="text-[11px] text-gray-400 pb-2.5 flex-shrink-0">
                  max {maxGuests}
                  {roomsBooked > 1 ? ` (${perRoomCapacity} × ${roomsBooked} rooms)` : ""}
                </p>
              </div>

              <div className="space-y-2">
                {Array.from({ length: form.guestCount }, (_, i) => (
                  <div key={i}>
                    <label htmlFor={`manual-guest-${i}`} className="block text-[11px] font-medium text-gray-500 mb-1">
                      {i === 0 ? "Lead guest *" : `Guest ${i + 1}`}
                    </label>
                    <input
                      id={`manual-guest-${i}`}
                      type="text"
                      value={form.guestNames[i] || ""}
                      onChange={(e) => setGuestName(i, e.target.value)}
                      placeholder={i === 0 ? "Full name" : "Full name (optional)"}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="manual-guest-0" className="block text-xs font-medium text-gray-500 mb-1">
                Guest Name *
              </label>
              <input
                id="manual-guest-0"
                type="text"
                value={form.guestNames[0] || ""}
                onChange={(e) => setGuestName(0, e.target.value)}
                placeholder="Full name"
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Pick a room type above to add more guests — the room's capacity sets the limit.
              </p>
            </div>
          )}

          {form.roomCount > 1 && (
            <p className="text-[11px] text-amber-600 bg-amber-50 rounded-lg px-3 py-1.5">
              This books {form.roomCount} identical rooms of this type for the same guest — the total/advance below is split evenly across them.
            </p>
          )}
          <div className="grid grid-cols-2 gap-3">
            {field("Total (₹)", "totalAmount", "number", { placeholder: "0", min: "0" })}
            {field("Advance (₹)", "advancePaid", "number", {
              placeholder: "0", min: "0",
            })}
          </div>
          {total > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex justify-between text-sm">
              <span className="text-amber-700">Balance Due</span>
              <span className="font-bold text-amber-800">
                ₹{(total - advance).toLocaleString("en-IN")}
              </span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Payment Method
              </label>
              <select
                value={form.paymentMethod}
                onChange={(e) =>
                  setForm((f) => ({ ...f, paymentMethod: e.target.value }))
                }
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {["Cash", "UPI", "Bank Transfer", "Other"].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Status
              </label>
              <select
                value={form.status}
                onChange={(e) =>
                  setForm((f) => ({ ...f, status: e.target.value }))
                }
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {["Pending", "Confirmed", "Blocked"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Remarks
            </label>
            <textarea
              value={form.remarks}
              onChange={(e) =>
                setForm((f) => ({ ...f, remarks: e.target.value }))
              }
              rows={2}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm
                focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              placeholder="Optional…"
            />
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer bg-gray-50 rounded-xl px-3 py-2.5">
            <input type="checkbox" checked={form.notifyGuest}
              onChange={(e) => setForm((f) => ({ ...f, notifyGuest: e.target.checked }))}
              className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-amber-500" />
            <span className="text-sm text-gray-700">
              Send confirmation + invoice to the guest
              <span className="block text-[11px] text-gray-400">
                {form.status === "Confirmed"
                  ? "Email, and WhatsApp where there's a number. Untick for a walk-in you'd rather not message — the invoice is still saved."
                  : "Only applies to a Confirmed booking with an amount. A Pending one notifies the guest when you approve it."}
              </span>
            </span>
          </label>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">
              {error}
            </p>
          )}
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white text-sm font-medium hover:from-amber-600 hover:to-orange-600 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Booking"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Payment modal ── */
function PaymentModal({ booking, onClose, onUpdated, authHeaders }) {
  // "Ad-hoc" bookings never had a real price on file — no total was ever set
  // (OTA bookings: the iCal feed never carries a price — some OTAs like
  // Airbnb collect payment themselves, others like Booking.com have the
  // guest pay at the property, but either way this PMS has no automatic
  // record of it; Manual bookings: admin left the total blank at creation
  // time). For those, entered amounts should keep
  // growing the total right along with the advance on every use, not just
  // the first — otherwise a booking would only ever accept one add-amount
  // before the total froze and the button disappeared. The `adHoc` marker
  // persists in payment once set so repeat use keeps working.
  const isAdHoc = booking.payment?.adHoc || !(booking.payment?.totalAmount > 0);
  const [amount, setAmount] = useState("");
  const [type, setType] = useState(isAdHoc ? "Full" : "Balance");
  const [method, setMethod] = useState("Cash");
  const [notifyGuest, setNotifyGuest] = useState(!!booking.guest?.whatsapp);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const parsedAmount = parseFloat(amount);
  const amountValid = Number.isFinite(parsedAmount) && parsedAmount > 0;

  async function submit() {
    if (!amountValid) { setError("Enter an amount greater than 0."); return; }
    setSaving(true);
    setError("");
    // The server recomputes advancePaid/totalAmount/remainingBalance/fullyPaid
    // atomically from the document's current state — sending only the raw
    // amount here (not a client-precomputed payment object) is what makes
    // that safe under concurrent payment recordings.
    try {
      const res = await fetch(`${API}/bookings/${booking._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({
          paymentAmount: parsedAmount,
          paymentType: type,
          paymentMethod: method,
          notifyGuest,
        }),
      });
      const json = await res.json();
      if (json.success) {
        onUpdated(json.data);
        onClose();
      } else {
        setError(json.error || "Couldn't record the payment. Please try again.");
      }
    } catch {
      setError("Couldn't reach the server. Please try again.");
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">
            {isAdHoc ? "Add Amount" : "Record Payment"}
          </h3>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-500">
            Booking: <strong>{booking.bookingRef}</strong>
          </p>
          {isAdHoc && (
            <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2">
              Enter the total amount for this booking.
            </p>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500 mb-1 block">
              Amount (₹)
            </label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setError(""); }}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
            />
          </div>
          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {["Advance", "Balance", "Full"].map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Method
              </label>
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              >
                {["Cash", "UPI", "Bank Transfer", "Other"].map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={notifyGuest}
              onChange={(e) => setNotifyGuest(e.target.checked)}
              disabled={!booking.guest?.whatsapp}
              className="rounded border-gray-300 text-amber-500 focus:ring-amber-400"
            />
            Send WhatsApp payment update to guest
            {!booking.guest?.whatsapp && (
              <span className="text-xs text-gray-400">
                (no WhatsApp on file)
              </span>
            )}
          </label>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !amountValid}
            className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500
              text-white text-sm font-medium disabled:opacity-60"
          >
            {saving ? "Saving…" : "Record"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Move a booking to different dates ──────────────────────────────────
   A guest asking to arrive a week later. Deliberately its own action rather
   than an editable field: the new dates have to be free, the physical room
   may change, and the nights being given up have to be released — to the
   calendar and to every connected OTA. The route does all of that; this
   just asks for the dates. ── */
function MoveDatesModal({ booking, onClose, onDone, authHeaders }) {
  // NOT toISOString().slice(0,10): a booking's dates are stored as local
  // midnight, which is the PREVIOUS day in UTC, so that pre-filled the
  // pickers one day behind and invited staff to save the wrong dates.
  const asDate = toLocalISODate;
  const [checkIn, setCheckIn]   = useState(asDate(booking.checkIn));
  const [checkOut, setCheckOut] = useState(asDate(booking.checkOut));
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const nights = Math.max(1, Math.round(
    (new Date(checkOut) - new Date(checkIn)) / 86400000));

  async function save() {
    setSaving(true); setError("");
    try {
      const res = await fetch(`${API}/bookings/${booking._id}/move`, {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ checkIn, checkOut }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.error || "Could not move this booking.");
      onDone?.(json.data);
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-5">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="p-2 rounded-xl bg-indigo-100 text-indigo-600"><CalendarClock size={18} /></span>
          <h3 className="font-bold text-gray-900">Move Booking</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          {booking.bookingRef} · currently {asDate(booking.checkIn)} → {asDate(booking.checkOut)}
        </p>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Check-in</span>
            <input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 min-h-[44px] text-sm
                focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </label>
          <label className="block">
            <span className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Check-out</span>
            <input type="date" value={checkOut} onChange={e => setCheckOut(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 min-h-[44px] text-sm
                focus:outline-none focus:ring-2 focus:ring-indigo-400" />
          </label>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          {nights} night{nights !== 1 ? "s" : ""}. The room is re-checked for the new dates, and the
          nights being given up go back on sale everywhere — including your connected OTAs.
          The amount on the booking is left as it is.
        </p>

        {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2 mt-3">{error}</p>}

        <div className="flex gap-2 mt-5">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 min-h-[44px] rounded-xl border border-gray-200 text-sm font-medium text-gray-700">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="flex-1 px-4 py-2.5 min-h-[44px] rounded-xl bg-indigo-600 text-white text-sm font-semibold
              hover:bg-indigo-700 disabled:opacity-60">
            {saving ? "Moving…" : "Move Booking"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Generic share-link modal (guest-form-link / review-link) ── */
function LinkShareModal({
  booking,
  endpoint,
  title,
  Icon,
  waMessage: waMessageFn,
  onClose,
  authHeaders,
}) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/bookings/${booking._id}/${endpoint}`, {
          method: "POST",
          headers: authHeaders(),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.error);
        setUrl(json.data.url);
      } catch (e) {
        setError(e.message || "Could not generate link");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  function copy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const guestName = booking.guest?.fullNames?.[0] || "Guest";
  const waMessage = encodeURIComponent(waMessageFn(guestName, url));
  const waNumber = (booking.guest?.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm">
      <div className="bg-white/75 backdrop-blur-2xl border border-white/50 rounded-2xl shadow-2xl shadow-black/10 w-full max-w-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <Icon size={15} className="text-amber-600" />
            </div>
            <h3 className="font-bold text-gray-900">{title}</h3>
          </div>
          <button onClick={onClose}>
            <X size={16} className="text-gray-400" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-xs text-gray-500 bg-gray-50 rounded-xl px-3 py-2">
            <strong>{guestName}</strong> · {booking.bookingRef}
          </p>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-gray-400 py-3">
              <RefreshCw size={14} className="animate-spin" /> Generating link…
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">
              {error}
            </p>
          ) : (
            <>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
                <span className="text-xs text-gray-700 flex-1 truncate">
                  {url}
                </span>
                <button
                  onClick={copy}
                  className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                >
                  {copied ? (
                    <Check size={13} className="text-green-600" />
                  ) : (
                    <Copy size={13} className="text-gray-500" />
                  )}
                </button>
              </div>
              <a
                href={`https://wa.me/${waNumber}?text=${waMessage}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl
                  bg-green-50 border border-green-200 text-green-700 font-medium text-sm hover:bg-green-100"
              >
                💬 Send via WhatsApp
              </a>
              <p className="text-xs text-gray-400">
                No login needed for the guest to open this link.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Icon-only action row for one booking — shared by the desktop table
   row and the mobile card view below, so the (long) set of conditional
   actions only needs to be written once. Flex-wrap is fine here: in the
   table it's overridden to flex-nowrap (a wide wrapped action row there
   used to blow out every row's height — see git history); in a card,
   each booking has its own independent height, so wrapping is harmless. ── */
function BookingActionButtons({
  b, wrap = false, checkingOut, checkOut,
  setApproveFor, setRejectFor, setPaymentFor, setShareFor, setReviewFor,
  setCancelFor, setNoShowFor, setFlagFor, setWaMsgFor, setMenuFor, setMoveFor, menuLinksOn,
  authHeaders,
}) {
  const isPending = b.status === "Pending";
  const isOTA = !["Website", "Manual"].includes(b.source);
  const afterCheckIn = new Date(b.checkIn) <= new Date();
  const noRealTotal = b.payment?.adHoc || !(b.payment?.totalAmount > 0);

  return (
    // gap-5 on the wrapping (phone card) variant, not gap-1: these icons are
    // 25px, and .tap-44 grows only the invisible hit area, so at a 4px gap two
    // neighbours' 44px areas overlap and each one effectively keeps ~29px of
    // width. 20px of gap puts them on a 45px pitch so every button really does
    // get its 44px. Desktop is a mouse pointer in a dense table row — unchanged.
    <div className={`flex items-center ${wrap ? "flex-wrap gap-5" : "flex-nowrap gap-1"}`}>
      {/* Approve / Reject — ANY Pending booking, however it was created.
        These used to be gated on source === "Website", so a manual entry
        saved as Pending showed no action at all, while the banner above
        still counted it as awaiting approval and the approve route itself
        never cared about the source. */}
      {isPending && (
        <>
          <button onClick={() => setApproveFor(b)} title="Approve"
            className="tap-44 p-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors">
            <ThumbsUp size={13} />
          </button>
          <button onClick={() => setRejectFor(b)} title="Reject"
            className="tap-44 p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
            <ThumbsDown size={13} />
          </button>
        </>
      )}
      {/* Payment button — while a pre-set balance remains, let the admin pay
        it down. Once there's no real total on file (OTA: the iCal feed
        carries no price, whether or not the guest already paid that
        particular platform upfront; Manual: total left blank at creation),
        switch to "Add Amount" so ad-hoc/manually-collected charges can
        still be logged after check-in, for any booking source. */}
      {!noRealTotal && b.payment?.remainingBalance > 0 && (
        <button onClick={() => setPaymentFor(b)} title="Record payment"
          className="tap-44 p-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
          <IndianRupee size={13} />
        </button>
      )}
      {noRealTotal && afterCheckIn && ["Confirmed", "Completed"].includes(b.status) && (
        <button onClick={() => setPaymentFor(b)} title="Add amount"
          className="tap-44 p-1.5 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors">
          <IndianRupee size={13} />
        </button>
      )}
      {/* Guest registration share link — OTA bookings only. Website/Manual
        bookings already collect full guest details (incl. travel from/to)
        at booking time; OTA sync (iCal) doesn't give us enough, so those
        still need the follow-up form. */}
      {b.status !== "Cancelled" && isOTA && (
        <button onClick={() => setShareFor(b)} title="Share registration form"
          className="tap-44 p-1.5 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">
          <Share2 size={13} />
        </button>
      )}
      {/* In-room menu link. Only for a Confirmed stay — the public order
          route refuses anything else, so offering it would hand out a link
          that immediately says ordering is closed. */}
      {menuLinksOn && b.status === "Confirmed" && (
        <button onClick={() => setMenuFor(b)} title="Share in-room menu link"
          className="tap-44 p-1.5 rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors">
          <UtensilsCrossed size={13} />
        </button>
      )}
      {/* Move dates — a guest asking to come a week later. Not offered once
        the stay is over or cancelled: there is nothing left to move, and the
        route refuses it anyway. */}
      {["Confirmed", "Pending", "Blocked"].includes(b.status) && (
        <button onClick={() => setMoveFor(b)} title="Move dates"
          className="tap-44 p-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors">
          <CalendarClock size={13} />
        </button>
      )}
      {/* Check out — marks Completed & auto-generates invoice */}
      {b.status === "Confirmed" && (
        <button onClick={() => checkOut(b)} disabled={checkingOut === b._id} title="Check out"
          className="tap-44 p-1.5 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors disabled:opacity-50">
          {checkingOut === b._id ? <RefreshCw size={13} className="animate-spin" /> : <CheckOutIcon size={13} />}
        </button>
      )}
      {/* Request review — Completed bookings only */}
      {b.status === "Completed" && (
        <button onClick={() => setReviewFor(b)} title="Request review"
          className="tap-44 p-1.5 rounded-lg bg-yellow-50 text-yellow-700 hover:bg-yellow-100 transition-colors">
          <Star size={13} />
        </button>
      )}
      {/* Cancel — Confirmed/Pending any time before checkout (a guest can
        cancel with the OTA same-day, or cut a stay short); Blocked can be
        lifted any time (no guest involved) */}
      {((["Confirmed", "Pending"].includes(b.status) && new Date(b.checkOut) > new Date()) ||
        b.status === "Blocked") && (
          <button onClick={() => setCancelFor(b)} title={b.status === "Blocked" ? "Remove block" : "Cancel booking"}
            className="tap-44 p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors">
            <X size={13} />
          </button>
        )}
      {/* No-show — guest had a Confirmed reservation and never arrived.
        No date restriction (unlike Cancel): this is discovered after the
        fact, often once checkout has already passed. Also available from
        Completed — the noon auto-checkout sweep has no way to know a
        no-show happened and may have already flipped it to Completed
        before anyone noticed. */}
      {["Confirmed", "Completed"].includes(b.status) && NO_SHOW_SOURCES.includes(b.source) && (
        <button onClick={() => setNoShowFor(b)} title="Mark as no-show"
          className="tap-44 p-1.5 rounded-lg bg-orange-50 text-orange-600 hover:bg-orange-100 transition-colors">
          <UserX size={13} />
        </button>
      )}
      {/* Invoice — a checked-out booking links straight to its pre-generated
        static PDF (no auth needed); otherwise this is a live on-demand
        preview, which sits behind admin auth so it has to be an
        authenticated fetch, not a plain link */}
      {b.invoiceUrl ? (
        <a href={b.invoiceUrl.startsWith("http") ? b.invoiceUrl : `${BE}${b.invoiceUrl}`}
          target="_blank" rel="noopener noreferrer" title="Invoice"
          className="tap-44 p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
          <FileText size={13} />
        </a>
      ) : (
        <button onClick={() => openInvoicePreview(b._id, authHeaders, API)} title="Invoice"
          className="tap-44 p-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
          <FileText size={13} />
        </button>
      )}
      {/* Flag/unflag — misconduct, misbehavior, property damage; follows the
        guest's phone number across future bookings */}
      {b.guest?.whatsapp && (
        <button onClick={() => setFlagFor(b)} title={b.guestFlag ? "Unflag guest" : "Flag guest"}
          className={`p-1.5 rounded-lg transition-colors
            ${b.guestFlag ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
          <Flag size={13} />
        </button>
      )}
      {/* Re-open the saved confirmation message any time — if the primary
        WhatsApp number is blocked/restricted, or the automatic send
        failed, send the same text via another number */}
      {b.guestMessageText && (
        <button onClick={() => setWaMsgFor(b)} title="WhatsApp message"
          className="tap-44 p-1.5 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 transition-colors">
          <MessageCircle size={13} />
        </button>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN BOOKINGS PAGE
══════════════════════════════════════════ */
export default function BookingsPage() {
  const { authHeaders, features } = useAuth();
  const { guestOrderingEnabled } = useSettings();
  // The Menu link only makes sense when BOTH gates are open — the platform's
  // POS flag and the property's own ordering toggle. The backend enforces the
  // same pair, so a hidden button and a refused request agree.
  const menuLinksOn = !!features?.pos && guestOrderingEnabled === true;
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [showManual, setShowManual] = useState(false);
  const [approveFor, setApproveFor] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [moveFor, setMoveFor]     = useState(null);
  const [noShowFor, setNoShowFor] = useState(null);
  const [paymentFor, setPaymentFor] = useState(null);
  const [shareFor, setShareFor] = useState(null);
  const [menuFor,  setMenuFor]  = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [flagFor, setFlagFor] = useState(null);
  const [waMsgFor, setWaMsgFor] = useState(null);
  const [showBlock, setShowBlock] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);

  async function checkOut(booking) {
    setCheckingOut(booking._id);
    try {
      const res = await fetch(`${API}/bookings/${booking._id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ status: "Completed" }),
      });
      const json = await res.json();
      if (json.success) updateInList(json.data);
    } catch {}
    setCheckingOut(null);
  }

  // Debounce the search box so it doesn't hit the server on every keystroke,
  // and jump back to page 1 whenever the query changes (a stale page number
  // from a previous, larger result set would otherwise come back empty).
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  // Guards against out-of-order responses: an earlier request (e.g. the
  // unfiltered initial load, which does more aggregation work) can resolve
  // after a later, narrower search request and clobber it. Only the most
  // recently *issued* request is allowed to write to state.
  const requestIdRef = useRef(0);
  const load = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20, sort: sortBy });
      if (sourceFilter !== "All") params.append("source", sourceFilter);
      if (statusFilter !== "All") params.append("status", statusFilter);
      if (debouncedSearch) params.append("search", debouncedSearch);
      const res = await fetch(`${API}/bookings?${params}`, {
        headers: authHeaders(),
      });
      const json = await res.json();
      if (reqId === requestIdRef.current) {
        setBookings(json.data || []);
        setTotalPages(json.pages || 1);
        setTotalCount(json.total ?? (json.data || []).length);
        setPendingCount(json.pendingCount ?? 0);
      }
    } catch {}
    if (reqId === requestIdRef.current) setLoading(false);
  }, [page, sourceFilter, statusFilter, debouncedSearch, sortBy]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = bookings;

  function updateInList(updated) {
    setBookings((prev) => {
      const before = prev.find((b) => b._id === updated._id);
      if (
        before &&
        before.status === "Pending" &&
        updated.status !== "Pending"
      ) {
        setPendingCount((c) => Math.max(0, c - 1));
      } else if (
        before &&
        before.status !== "Pending" &&
        updated.status === "Pending"
      ) {
        setPendingCount((c) => c + 1);
      }
      return prev.map((b) => (b._id === updated._id ? updated : b));
    });
  }

  // A flag is keyed by phone number, not a single booking — apply it to
  // every currently-listed row sharing that guest's number so the whole
  // page reflects it immediately, without needing a refetch.
  function updateGuestFlagInList(phone, flag) {
    setBookings((prev) =>
      prev.map((b) =>
        normalizePhone(b.guest?.whatsapp) === phone ? { ...b, guestFlag: flag } : b,
      ),
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          <div className="flex items-center gap-3 mt-0.5">
            <p className="text-sm text-gray-500">{totalCount} total</p>
            {pendingCount > 0 && (
              <span
                className="text-xs font-semibold bg-amber-100 text-amber-700
                px-2 py-0.5 rounded-full animate-pulse"
              >
                {pendingCount} pending approval
              </span>
            )}
          </div>
        </div>
        {/* flex-wrap — without it, this row of 5 buttons (including the
            primary "Manual Entry" CTA) silently overflowed past the
            viewport on mobile with no way to reach it: the admin shell's
            <main> clips horizontal overflow instead of scrolling it, so an
            unwrapped toolbar here doesn't just look bad, it makes the
            button unreachable. */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={load}
            className="tap-44 p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <RefreshCw
              size={16}
              className={`text-gray-500 ${loading ? "animate-spin" : ""}`}
            />
          </button>
          <button
            onClick={() =>
              downloadWithAuth(
                `${API}/reports/csv`,
                authHeaders,
                "bookings.csv",
              ).catch((e) => alert(e.message))
            }
            className="tap-44 flex items-center gap-2 text-sm font-medium text-gray-600
              bg-white border border-gray-200 px-3 py-2 rounded-xl shadow-sm hover:shadow transition-all"
          >
            <FileDown size={15} /> Export CSV
          </button>
          <button
            onClick={() => setShowBlock(true)}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-gray-600
              bg-white border border-gray-200 px-3 py-2 rounded-xl shadow-sm hover:shadow transition-all"
          >
            <CalendarOff size={15} className="text-gray-500" /> Block Dates
          </button>
          <button
            onClick={() => setShowManual(true)}
            className="tap-44 flex items-center gap-2 text-sm font-medium text-white
              bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-xl
              shadow-sm hover:from-amber-600 hover:to-orange-600 transition-all"
          >
            <Plus size={15} /> Manual Entry
          </button>
        </div>
      </div>

      {/* Pending requests banner */}
      {pendingCount > 0 && (
        <div
          className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4
          flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-bold">
                {pendingCount}
              </span>
            </div>
            <div>
              <p className="font-semibold text-amber-800 text-sm">
                {pendingCount} booking request{pendingCount > 1 ? "s" : ""}{" "}
                awaiting your approval
              </p>
              <p className="text-amber-600 text-xs">
                Guests are waiting — approve or reject below
              </p>
            </div>
          </div>
          <button
            onClick={() => setStatusFilter("Pending")}
            className="text-xs font-semibold text-amber-700 bg-amber-100
              hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
          >
            Show Pending
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={15}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guests, ref, phone…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl
              focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setPage(1);
          }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
        >
          <option value="All">All Sources</option>
          {["Website", "Airbnb", "Booking.com", "Agoda", "MakeMyTrip", "Manual"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
        >
          <option value="All">All Statuses</option>
          {["Pending", "Confirmed", "Completed", "Cancelled", "Blocked", "No-Show"].map(
            (s) => (
              <option key={s}>{s}</option>
            ),
          )}
        </select>
        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value);
            setPage(1);
          }}
          className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
        >
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="checkInAsc">Check-in (Earliest)</option>
          <option value="checkInDesc">Check-in (Latest)</option>
          <option value="amountDesc">Amount (High→Low)</option>
          <option value="amountAsc">Amount (Low→High)</option>
          <option value="balanceDesc">Balance Due (High→Low)</option>
          <option value="refAsc">Booking Ref (A→Z)</option>
        </select>
      </div>

      {/* Table — sized to its content (no forced w-full) so columns never
          get artificially squeezed; the wrapper scrolls horizontally on
          narrow viewports instead of truncating data. Compact by design:
          text-xs throughout and icon-only actions, so a wide row of admin
          actions can't blow out every row's height the way stacked
          text-label buttons used to. */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Table — tablet and up. Below md, an 11-column table has no good
            answer (shrink illegibly or force horizontal scroll), so mobile
            gets its own card layout instead — see the md:hidden block below. */}
        <div className="hidden md:block overflow-auto max-h-[70vh]">
          <table className="text-xs">
            <thead className="sticky top-0 z-10 bg-white">
              <tr className="border-b border-gray-100">
                {[
                  "Ref",
                  "Guest",
                  "Room",
                  "Source",
                  "Check-in",
                  "Check-out",
                  "Nights",
                  "Amount",
                  "Balance",
                  "Status",
                  "Actions",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-left text-[10.5px] font-semibold text-gray-400
                    uppercase tracking-wider px-3 py-2 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-gray-400">
                    <RefreshCw
                      size={20}
                      className="animate-spin mx-auto mb-2"
                    />{" "}
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-12 text-gray-400">
                    No bookings found
                  </td>
                </tr>
              ) : (
                filtered.map((b) => {
                  const nights = Math.ceil(
                    (new Date(b.checkOut) - new Date(b.checkIn)) / 86400000,
                  );
                  const gName = bookingLabel(b, "—");
                  const totalGuests =
                    b.guest?.numberOfGuests || b.guestCount || 1;
                  const extraGuests = totalGuests - 1;
                  const dot = SOURCE_DOT[b.source] || "bg-gray-400";
                  const isPending = b.status === "Pending";

                  return (
                    <tr
                      key={b._id}
                      className={`hover:bg-gray-50/50 transition-colors
                      ${isPending ? "bg-amber-50/30" : ""}`}
                    >
                      <td className="px-3 py-2 font-mono text-[10.5px] text-gray-500 whitespace-nowrap">
                        {b.bookingRef}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <p className="font-medium text-gray-900 truncate max-w-[130px]">
                            {gName}
                            {extraGuests > 0 && (
                              <span className="text-gray-400">
                                +{extraGuests}
                              </span>
                            )}
                          </p>
                          {b.guestFlag && (
                            <span title={b.guestFlag.reason}
                              className="flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5
                                rounded-full bg-red-50 text-red-700 border border-red-200
                                whitespace-nowrap flex-shrink-0">
                              <Flag size={9} /> Flagged
                            </span>
                          )}
                        </div>
                        {b.guest?.whatsapp && (
                          <p className="text-[10.5px] text-gray-400">
                            {b.guest.whatsapp}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {b.room ? (
                          <div className="flex items-center gap-1.5">
                            {b.unitRoomNumber && (
                              <span className="w-5 h-5 flex-shrink-0 rounded-md bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center">
                                {b.unitRoomNumber}
                              </span>
                            )}
                            <span className="text-gray-700 truncate max-w-[170px]" title={b.room.name}>
                              {b.room.name}
                            </span>
                          </div>
                        ) : (
                          <span className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 whitespace-nowrap">
                            <AlertTriangle size={9} /> No room
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                          <span className="text-gray-700">{b.source}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                        {new Date(b.checkIn).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        })}
                      </td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                        {new Date(b.checkOut).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                        })}
                      </td>
                      <td className="px-3 py-2 text-center text-gray-700">
                        {nights}
                      </td>
                      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                        {b.payment?.totalAmount > 0
                          ? `₹${b.payment.totalAmount.toLocaleString("en-IN")}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {b.payment?.remainingBalance > 0 ? (
                          <span className="text-rose-600 font-medium">
                            ₹
                            {b.payment.remainingBalance.toLocaleString("en-IN")}
                          </span>
                        ) : b.payment?.totalAmount > 0 ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <Check size={11} /> Paid
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border
                        ${STATUS_COLORS[b.status] || STATUS_COLORS.Pending}`}
                        >
                          {b.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <BookingActionButtons b={b} checkingOut={checkingOut} checkOut={checkOut}
                          setApproveFor={setApproveFor} setRejectFor={setRejectFor} setPaymentFor={setPaymentFor}
                          setShareFor={setShareFor} setReviewFor={setReviewFor} setCancelFor={setCancelFor} setNoShowFor={setNoShowFor} setMoveFor={setMoveFor}
                          setFlagFor={setFlagFor} setWaMsgFor={setWaMsgFor}
                      setMenuFor={setMenuFor} menuLinksOn={menuLinksOn} authHeaders={authHeaders} />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Card list — mobile only (below md). Same data, same actions,
            laid out so nothing needs truncating or horizontal scrolling. */}
        <div className="md:hidden divide-y divide-gray-100 max-h-[75vh] overflow-auto">
          {loading ? (
            <div className="text-center py-12 text-gray-400">
              <RefreshCw size={20} className="animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-400">No bookings found</div>
          ) : (
            filtered.map((b) => {
              const nights = Math.ceil((new Date(b.checkOut) - new Date(b.checkIn)) / 86400000);
              const gName = bookingLabel(b, "—");
              const totalGuests = b.guest?.numberOfGuests || b.guestCount || 1;
              const extraGuests = totalGuests - 1;
              const dot = SOURCE_DOT[b.source] || "bg-gray-400";
              const isPending = b.status === "Pending";
              const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

              return (
                <div key={b._id} className={`p-3.5 space-y-2.5 ${isPending ? "bg-amber-50/30" : ""}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-mono text-[10px] text-gray-400">{b.bookingRef}</p>
                      <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <p className="font-semibold text-gray-900 text-sm truncate">
                          {gName}
                          {extraGuests > 0 && <span className="text-gray-400 font-normal"> +{extraGuests}</span>}
                        </p>
                        {b.guestFlag && (
                          <span title={b.guestFlag.reason}
                            className="flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5
                              rounded-full bg-red-50 text-red-700 border border-red-200 whitespace-nowrap flex-shrink-0">
                            <Flag size={9} /> Flagged
                          </span>
                        )}
                      </div>
                      {b.guest?.whatsapp && <p className="text-xs text-gray-400">{b.guest.whatsapp}</p>}
                    </div>
                    <span className={`text-[10.5px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0
                      ${STATUS_COLORS[b.status] || STATUS_COLORS.Pending}`}>
                      {b.status}
                    </span>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-xs">
                    {b.room ? (
                      <div className="flex items-center gap-1.5 min-w-0">
                        {b.unitRoomNumber && (
                          <span className="w-5 h-5 flex-shrink-0 rounded-md bg-gray-900 text-white text-[10px] font-bold flex items-center justify-center">
                            {b.unitRoomNumber}
                          </span>
                        )}
                        <span className="text-gray-700 truncate">{b.room.name}</span>
                      </div>
                    ) : (
                      <span className="flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 whitespace-nowrap">
                        <AlertTriangle size={9} /> No room
                      </span>
                    )}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                      <span className="text-gray-500">{b.source}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{fmtDate(b.checkIn)} – {fmtDate(b.checkOut)}</span>
                    <span>{nights} night{nights !== 1 ? "s" : ""}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs border-t border-gray-100 pt-2">
                    <span className="text-gray-500">
                      {b.payment?.totalAmount > 0 ? `₹${b.payment.totalAmount.toLocaleString("en-IN")}` : "No total set"}
                    </span>
                    {b.payment?.remainingBalance > 0 ? (
                      <span className="text-rose-600 font-medium">₹{b.payment.remainingBalance.toLocaleString("en-IN")} due</span>
                    ) : b.payment?.totalAmount > 0 ? (
                      <span className="flex items-center gap-1 text-green-600"><Check size={11} /> Paid</span>
                    ) : null}
                  </div>

                  <div className="border-t border-gray-100 pt-2.5">
                    <BookingActionButtons b={b} wrap checkingOut={checkingOut} checkOut={checkOut}
                      setApproveFor={setApproveFor} setRejectFor={setRejectFor} setPaymentFor={setPaymentFor}
                      setShareFor={setShareFor} setReviewFor={setReviewFor} setCancelFor={setCancelFor} setNoShowFor={setNoShowFor} setMoveFor={setMoveFor}
                      setFlagFor={setFlagFor} setWaMsgFor={setWaMsgFor}
                      setMenuFor={setMenuFor} menuLinksOn={menuLinksOn} authHeaders={authHeaders} />
                  </div>
                </div>
              );
            })
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="text-sm px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Modals */}
      {showManual && (
        <ManualBookingModal
          onClose={() => setShowManual(false)}
          onSaved={(arr) => setBookings((p) => [...arr, ...p])}
          authHeaders={authHeaders}
        />
      )}
      {showBlock && (
        <BlockDatesModal
          onClose={() => setShowBlock(false)}
          onSaved={(arr) => setBookings((p) => [...arr, ...p])}
          authHeaders={authHeaders}
        />
      )}
      {flagFor && (
        <FlagGuestModal
          phone={normalizePhone(flagFor.guest?.whatsapp)}
          guestName={bookingLabel(flagFor, "Guest")}
          existingFlag={flagFor.guestFlag}
          onClose={() => setFlagFor(null)}
          onSaved={(flag) => updateGuestFlagInList(normalizePhone(flagFor.guest?.whatsapp), flag)}
          authHeaders={authHeaders}
        />
      )}
      {waMsgFor && (
        <ApprovalWhatsAppShare
          booking={waMsgFor}
          onClose={() => setWaMsgFor(null)}
        />
      )}
      {approveFor && (
        <ApproveModal
          booking={approveFor}
          onClose={() => setApproveFor(null)}
          onDone={updateInList}
          authHeaders={authHeaders}
        />
      )}
      {rejectFor && (
        <ReasonModal
          booking={rejectFor}
          endpoint="reject"
          title="Reject Booking"
          verb="Reject & Notify"
          savingVerb="Rejecting…"
          onClose={() => setRejectFor(null)}
          onDone={updateInList}
          authHeaders={authHeaders}
        />
      )}
      {moveFor && (
        <MoveDatesModal
          booking={moveFor}
          onClose={() => setMoveFor(null)}
          onDone={updateInList}
          authHeaders={authHeaders}
        />
      )}
      {cancelFor && (
        <ReasonModal
          booking={cancelFor}
          endpoint="cancel"
          title="Cancel Booking"
          verb="Cancel & Notify"
          savingVerb="Cancelling…"
          onClose={() => setCancelFor(null)}
          onDone={updateInList}
          authHeaders={authHeaders}
        />
      )}
      {noShowFor && (
        <ReasonModal
          booking={noShowFor}
          endpoint="no-show"
          title="Mark as No-Show"
          verb="Mark No-Show"
          savingVerb="Saving…"
          notifiesGuest={false}
          Icon={UserX}
          iconClasses="bg-orange-100 text-orange-500"
          buttonClasses="bg-orange-500 hover:bg-orange-600"
          onClose={() => setNoShowFor(null)}
          onDone={updateInList}
          authHeaders={authHeaders}
        />
      )}
      {paymentFor && (
        <PaymentModal
          booking={paymentFor}
          onClose={() => setPaymentFor(null)}
          onUpdated={updateInList}
          authHeaders={authHeaders}
        />
      )}
      {shareFor && (
        <LinkShareModal
          booking={shareFor}
          endpoint="guest-form-link"
          title="Guest Registration Link"
          Icon={Share2}
          waMessage={(name, url) =>
            `Hi ${name}, please fill in your guest details for booking ${shareFor.bookingRef}: ${url}`
          }
          onClose={() => setShareFor(null)}
          authHeaders={authHeaders}
        />
      )}
      {menuFor && (
        <LinkShareModal
          booking={menuFor}
          endpoint="menu-link"
          title="In-Room Menu Link"
          Icon={UtensilsCrossed}
          waMessage={(name, url) =>
            `Hi ${name}, you can order from our menu here — it's charged to your room: ${url}`
          }
          onClose={() => setMenuFor(null)}
          authHeaders={authHeaders}
        />
      )}
      {reviewFor && (
        <LinkShareModal
          booking={reviewFor}
          endpoint="review-link"
          title="Review Request Link"
          Icon={Star}
          waMessage={(name, url) =>
            `Hi ${name}, thanks for staying with us! We'd love your feedback: ${url}`
          }
          onClose={() => setReviewFor(null)}
          authHeaders={authHeaders}
        />
      )}
    </div>
  );
}
